import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import {
  Download,
  Share,
  Plus,
  MoreVertical,
  MonitorDown,
  CheckCircle2,
  Apple,
  Smartphone,
  ArrowLeft,
} from "lucide-react";

function detectPlatform() {
  const ua = navigator.userAgent || "";
  const isIOS = /iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /android/i.test(ua);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
  return { isIOS, isAndroid, isStandalone, isDesktop: !isIOS && !isAndroid };
}

function Step({ n, icon: Icon, children }) {
  return (
    <li className="flex items-start gap-4">
      <span className="w-8 h-8 shrink-0 flex items-center justify-center border border-primary/50 bg-primary/10 font-mono text-primary text-sm">
        {n}
      </span>
      <div className="flex items-center gap-2 pt-1 text-foreground/90">
        {Icon && <Icon size={18} strokeWidth={1.5} className="text-primary shrink-0" />}
        <span className="text-sm">{children}</span>
      </div>
    </li>
  );
}

export default function Install() {
  const [plat, setPlat] = useState(detectPlatform());
  const [canPrompt, setCanPrompt] = useState(!!window.__plyxInstallPrompt);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    setPlat(detectPlatform());
    const onInstallable = () => setCanPrompt(true);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("plyx-installable", onInstallable);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("plyx-installable", onInstallable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const doInstall = async () => {
    const p = window.__plyxInstallPrompt;
    if (!p) return;
    p.prompt();
    const choice = await p.userChoice;
    if (choice?.outcome === "accepted") setInstalled(true);
    window.__plyxInstallPrompt = null;
    setCanPrompt(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid-bg">
      <header className="border-b border-border bg-card/80 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/" className="font-mono text-sm text-muted-foreground hover:text-primary flex items-center gap-1.5">
            <ArrowLeft size={15} strokeWidth={1.5} /> Home
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex w-16 h-16 items-center justify-center border border-primary/50 bg-primary/10 glow-green mb-6">
            <Download size={30} strokeWidth={1.5} className="text-primary" />
          </div>
          <h1 className="font-mono font-bold tracking-tighter text-3xl md:text-4xl">Install Plyx Cyber Guard</h1>
          <p className="mt-3 text-muted-foreground">
            Add the app to your device for one-tap access — no app store, no download wait.
          </p>
        </div>

        {installed || plat.isStandalone ? (
          <div data-testid="install-done" className="border border-primary/50 bg-primary/10 p-8 text-center">
            <CheckCircle2 size={36} strokeWidth={1.5} className="text-primary mx-auto mb-3" />
            <div className="font-mono font-bold text-lg text-primary">You're all set!</div>
            <p className="text-sm text-muted-foreground mt-1">Plyx Cyber Guard is installed. Launch it from your home screen.</p>
            <Link to="/app">
              <Button className="mt-6 rounded-none font-mono bg-primary text-primary-foreground hover:bg-primary/90 glow-green">
                Open the app
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Android / Desktop: one-tap button when available */}
            {(plat.isAndroid || plat.isDesktop) && canPrompt && (
              <div data-testid="install-card-auto" className="border border-primary/50 bg-card p-6 text-center">
                <p className="font-mono text-sm text-muted-foreground mb-4">
                  Your browser supports one-tap install.
                </p>
                <Button
                  data-testid="install-now-button"
                  onClick={doInstall}
                  size="lg"
                  className="rounded-none font-mono bg-primary text-primary-foreground hover:bg-primary/90 glow-green"
                >
                  <Download size={18} strokeWidth={1.5} className="mr-2" /> Install now
                </Button>
              </div>
            )}

            {/* iOS instructions */}
            {plat.isIOS && (
              <div data-testid="install-ios" className="border border-border bg-card p-6">
                <div className="flex items-center gap-2 font-mono font-bold mb-4">
                  <Apple size={18} strokeWidth={1.5} className="text-primary" /> On iPhone / iPad (Safari)
                </div>
                <ol className="space-y-4">
                  <Step n="1" icon={Share}>Tap the <b>Share</b> button (the square with an arrow) in Safari's toolbar.</Step>
                  <Step n="2" icon={Plus}>Scroll down and tap <b>“Add to Home Screen”</b>.</Step>
                  <Step n="3" icon={CheckCircle2}>Tap <b>Add</b> — the Plyx icon appears on your home screen.</Step>
                </ol>
                <p className="mt-4 text-xs text-muted-foreground/80 font-mono">
                  Note: Apple only allows installing via Safari's Share menu — this is the official way on iOS.
                </p>
              </div>
            )}

            {/* Android manual fallback */}
            {plat.isAndroid && (
              <div data-testid="install-android" className="border border-border bg-card p-6">
                <div className="flex items-center gap-2 font-mono font-bold mb-4">
                  <Smartphone size={18} strokeWidth={1.5} className="text-primary" /> On Android (Chrome) — manual
                </div>
                <ol className="space-y-4">
                  <Step n="1" icon={MoreVertical}>Tap the <b>⋮</b> menu (top-right) in Chrome.</Step>
                  <Step n="2" icon={Download}>Tap <b>“Install app”</b> or <b>“Add to Home screen”</b>.</Step>
                  <Step n="3" icon={CheckCircle2}>Confirm — the app installs to your home screen.</Step>
                </ol>
              </div>
            )}

            {/* Desktop manual fallback */}
            {plat.isDesktop && (
              <div data-testid="install-desktop" className="border border-border bg-card p-6">
                <div className="flex items-center gap-2 font-mono font-bold mb-4">
                  <MonitorDown size={18} strokeWidth={1.5} className="text-primary" /> On Desktop (Chrome / Edge)
                </div>
                <ol className="space-y-4">
                  <Step n="1" icon={MonitorDown}>Click the <b>install icon</b> (a monitor with a down-arrow) at the right of the address bar.</Step>
                  <Step n="2" icon={CheckCircle2}>Click <b>Install</b> — Plyx opens in its own window.</Step>
                </ol>
                <p className="mt-4 text-xs text-muted-foreground/80 font-mono">
                  Don't see the icon? Open the browser menu → “Install Plyx Cyber Guard”.
                </p>
              </div>
            )}

            <div className="text-center pt-2">
              <Link to="/app" className="font-mono text-sm text-primary hover:underline">
                Or just use it in the browser →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
